import { takeEvery, select, put, delay, call, spawn, takeLatest } from 'redux-saga/effects';
import { matchPath } from 'react-router';

import { ui as uiActions, viewer as viewerActions } from 'flux/actions';
import { LOCATION_CHANGE } from 'connected-react-router';

import {
  ui as uiSelectors,
  property as propertySelectors,
  viewer as viewerSelectors,
  application as applicationSelectors,
} from 'flux/selectors';
import { ui as uiInitialState } from 'flux/initialState';

import { pathDrawerRules } from 'app/routes';
import { DIRECTION_HORIZONTAL } from '@doveconviene/react-sf-viewer/lib/shared/utils/consts';
import basicTheme from 'basicTheme';
import errorHandler from 'errorHandler';

export function* onLocationChange({ payload }) {
  const {
    location: { pathname },
  } = payload;

  const pathRule = Object.keys(pathDrawerRules).find((path) => matchPath(pathname, { path }));
  const newState = pathRule ? pathDrawerRules[pathRule] : uiInitialState.drawer.active;

  const { active: prevState } = yield select(uiSelectors.selectDrawer);
  if (newState !== prevState) yield put(uiActions.toggleDrawer(newState));
}

export function* onViewerLastPage() {
  // TODO: remove hard-coded CTA index
  const cta = yield select(propertySelectors.selectPropertyCTA, 0);
  if (cta && cta.is_active) {
    const { active } = yield select(uiSelectors.selectModal);
    yield delay(active ? 0 : process.env.REACT_APP_MODAL_TIMEOUT);
    yield put(uiActions.toggleModal(!active));
  }
}

export function* onViewerEvent({ payload: { isLast } }) {
  if (isLast) yield spawn(onViewerLastPage);
}

export function* onToggleFullscreenButton(visible) {
  const { supported } = yield select(applicationSelectors.selectApplicationFullscreen);

  if (supported) {
    yield put(uiActions.toggleFullscreenButton(visible));
  }
}

export function* onViewerZoomed({ payload: { zoomed } }) {
  const navigatorOpen = yield select(viewerSelectors.selectViewerNavigatorOpen);
  const shouldBeVisible = !zoomed && !navigatorOpen;
  yield call(onToggleFullscreenButton, shouldBeVisible);
}

export function* onViewerNavigatorOpen({ payload: { navigatorOpen } }) {
  const zoomed = yield select(viewerSelectors.selectViewerZoomed);
  const shouldBeVisible = !zoomed && !navigatorOpen;
  yield call(onToggleFullscreenButton, shouldBeVisible);
}

export function* onViewerTap() {
  const { zoomed, navigatorOpen } = yield select(viewerSelectors.selectViewer);

  const shouldBeVisible = !zoomed && !navigatorOpen;
  yield call(onToggleFullscreenButton, shouldBeVisible);
}

export function* initVerticalScroll(action) {
  try {
    const { deviceWidth } = action.payload;
    const isVerticalScrollEnabled = deviceWidth <= basicTheme.breakpoints.values.md;
    yield put({
      type: uiActions.types.SET_IS_VERTICAL_SCROLL_ENABLED,
      payload: { isVerticalScrollEnabled },
    });
    if (!isVerticalScrollEnabled) {
      yield put({
        type: viewerActions.types.SET_DIRECTION,
        payload: { direction: DIRECTION_HORIZONTAL },
      });
    }
  } catch (e) {
    errorHandler(e, true);
  }
}

export function* resetHeight() {
  yield window.dispatchEvent(new Event('resize'));
}

export default function* () {
  yield takeEvery(LOCATION_CHANGE, onLocationChange);
  yield takeEvery(
    [
      viewerActions.types.INITIALIZE,
      viewerActions.types.WINDOW_RESIZE_CHANGED_PAGES,
      viewerActions.types.BROWSE,
    ],
    onViewerEvent,
  );
  yield takeEvery(viewerActions.types.ZOOMED, onViewerZoomed);
  yield takeEvery(viewerActions.types.NAVIGATOR_OPEN, onViewerNavigatorOpen);
  yield takeEvery(viewerActions.types.TAP, onViewerTap);
  yield takeLatest(uiActions.types.SET_DEVICE_WIDTH, initVerticalScroll);
  yield takeLatest(uiActions.types.TOGGLE_BANNER, resetHeight);
}
